/**
 * Singleton HTMLVideoElement that surfaces wherever the app shows a
 * video, modeled on green hornet's `videoElement` in
 * PlayerSingleton.ts.
 *
 * Why a singleton: Vinyl's MediaPlayer (and friends) want a stable
 * mediaElement reference for the lifetime of the session — re-creating
 * a `<video>` per page tears down playback state, breaks DRM
 * negotiation, and forces every play to re-buffer. Holding one element
 * and moving its DOM parent solves all three.
 *
 * The element lives detached from the DOM tree by default
 * (`display: none` while parked on `document.body`); a stage container
 * "attaches" it on connect, "detaches" on disconnect. While detached,
 * playback is paused and the source URL stays loaded — the element is
 * effectively dormant until something attaches it again.
 *
 * The element is the media of the `'vinylMedia'` Vinyl engine, which plays
 * podcasts, music videos and every other non-catalog source. Catalog tracks
 * play on their own element (`catalogAudioElement`), so the visualizer's
 * permanent tap never reaches this one.
 */

import { createLogPrefix, logDebug } from '@amazon/vinyl-util'
import { combineData, data, externalData, type ObservableValue } from '@amazon/vinyl-observable'
import { documentEvents } from '@/util/interaction'

const target = { logPrefix: createLogPrefix('videoStage') }

let videoElement: HTMLVideoElement | null = null

/**
 * Fires when the user dismisses the PiP window via its built-in close (X)
 * button — distinguished from the "back to tab" button because the X
 * pauses the element before emitting `leavepictureinpicture`. Listeners
 * typically respond by clearing the playlist (the user's intent is "stop
 * playing"); the back-to-tab path leaves the element playing so we keep
 * the playlist intact.
 */
const pipCloseListeners = new Set<() => void>()

export function onPipClose(listener: () => void): () => void {
    pipCloseListeners.add(listener)
    return () => pipCloseListeners.delete(listener)
}

/**
 * Reflects whether the current playlist entry should render the video
 * stage. Driven authoritatively by the queue source via
 * {@link setHasVideo} — the playlist binding flips it based on
 * `PlaylistEntry.video`. We avoid sniffing `media.videoWidth` because
 * that flips late (after `loadedmetadata`) and races with the stage's
 * mount/unmount.
 */
const hasVideoRef = data(false)
const inPipRef = data(false)

/**
 * Whether the stage's floating controls (close / captions / PiP /
 * fullscreen) are currently shown. The overlay drives this from a
 * mouse-inactivity timer (activity shows; idle hides) alongside the
 * existing focus-out hide. Shown by default so a freshly-mounted stage
 * never starts with its controls hidden. Module-scoped so the chat
 * input bar can hide in lock-step (see {@link getStageChromeHidden}).
 */
const controlsVisibleRef = data(true)

/**
 * Seconds of pointer inactivity before the stage controls auto-hide.
 * Shared so the stage's own idle timer (VideoStageOverlay) and the
 * fullscreen-chrome idle timer (AppView) dwell for the same duration —
 * the chat input bar tracks {@link getStageChromeHidden} in both, so a
 * single dwell keeps its fade identical in and out of fullscreen.
 */
export const STAGE_CONTROLS_HIDE_SECONDS = 3

export function getHasVideo(): ObservableValue<boolean> {
    return hasVideoRef
}

/**
 * Tells the stage whether the current playlist entry is a video.
 * Called from the playlist→player binding when `current` changes.
 */
export function setHasVideo(v: boolean): void {
    hasVideoRef.value = v
}

/**
 * True when the current entry is a video and it isn't handed off to
 * picture-in-picture — i.e. the in-app stage should render the picture.
 * The stage overlay renders on it; the detail page uses it to decide
 * whether the poster's play button should still show.
 */
const stageVisible$ = combineData({ hasVideo: hasVideoRef, inPip: inPipRef }).map(
    ({ hasVideo, inPip }) => hasVideo && !inPip
)

export function getStageVisible(): ObservableValue<boolean> {
    return stageVisible$
}

export function getStageControlsVisible(): ObservableValue<boolean> {
    return controlsVisibleRef
}

/**
 * True when the browser blocked unmuted video autoplay and we fell back to
 * muted playback (see setVideoQueue's playWithAutoplayFallback). The stage
 * surfaces a tap-to-unmute affordance while set; it clears once the user
 * unmutes (from the affordance or the transport) or a fresh video is queued.
 */
const autoMutedRef = data(false)

export function getVideoAutoMuted(): ObservableValue<boolean> {
    return autoMutedRef
}

export function setVideoAutoMuted(v: boolean): void {
    autoMutedRef.value = v
}

/**
 * Show / hide the stage's floating controls. Driven by the overlay's
 * mouse-inactivity timer and focus handlers.
 */
export function setStageControlsVisible(v: boolean): void {
    controlsVisibleRef.value = v
}

/**
 * True only when the in-app video stage is showing AND its controls are
 * currently auto-hidden. The chat input bar keys off this to fade out
 * together with the overlay controls — gated on stage visibility so it
 * never affects normal audio / browse screens where no video is staged.
 */
const stageChromeHidden$ = combineData({
    stageVisible: stageVisible$,
    controlsVisible: controlsVisibleRef,
}).map(({ stageVisible, controlsVisible }) => stageVisible && !controlsVisible)

export function getStageChromeHidden(): ObservableValue<boolean> {
    return stageChromeHidden$
}

/**
 * Returns the lazily-created singleton video element. Safe to call on
 * any thread / in any lifecycle phase — the element is created on first
 * call and parked off-screen.
 */
export function getVideoElement(): HTMLVideoElement {
    if (videoElement) return videoElement
    const el = document.createElement('video')
    // Native controls are off — playback is driven by the global
    // transport. Keep `controlsList` defensive for any user-agents
    // that surface their own UI on right-click / picture-in-picture.
    el.controls = false
    // Defensive: some user agents surface their own controls on
    // right-click / cast targets even when controls=false. The
    // `controlsList` API isn't in lib.dom.d.ts but is widely
    // supported on Chromium and ignored elsewhere.
    const list = (el as HTMLVideoElement & { controlsList?: DOMTokenList }).controlsList
    list?.add('nodownload', 'nofullscreen', 'noremoteplayback')
    el.playsInline = true
    el.preload = 'metadata'
    el.style.display = 'none'
    el.style.width = '100%'
    el.style.height = '100%'
    el.style.objectFit = 'contain'
    el.style.backgroundColor = '#000'
    document.body.appendChild(el)
    videoElement = el

    // The PiP window has two exits: "back to tab" (keeps playing) and an
    // X close (pauses, then leaves). The browser fires the same
    // `leavepictureinpicture` for both. The only differentiator is the
    // playback state shortly after — back-to-tab leaves it playing,
    // X-close leaves it paused. The `pause` event can land slightly
    // after `leavepictureinpicture` (varies by browser/device), so we
    // sample `el.paused` after a delay long enough to absorb that race
    // on slower devices. 250ms is well below human-perceptible "the
    // close didn't do anything" while still letting the pause settle.
    el.addEventListener('enterpictureinpicture', () => {
        inPipRef.value = true
    })
    el.addEventListener('leavepictureinpicture', () => {
        inPipRef.value = false
        setTimeout(() => {
            if (!el.paused) return
            logDebug(target, 'PiP closed via X — notifying listeners')
            for (const fn of pipCloseListeners) {
                try {
                    fn()
                } catch {
                    // listeners are isolated
                }
            }
        }, 250)
    })

    return el
}

/**
 * Move the singleton video element into `container`, making it visible.
 * Returns a disposer that parks the element back on `document.body`
 * (still display: none). Playback is NOT interrupted by attach /
 * detach — the same `<video>` keeps playing across moves, which is
 * how PiP can hand control off to the OS chrome while audio
 * continues from the same element.
 */
export function attachVideoToStage(container: HTMLElement): () => void {
    const el = getVideoElement()
    el.style.display = ''
    container.appendChild(el)
    logDebug(target, 'attached')
    return () => {
        el.style.display = 'none'
        document.body.appendChild(el)
        logDebug(target, 'detached')
    }
}

/**
 * Hand the singleton `<video>` to picture-in-picture, or return it to the
 * page if it's already there. Rejections are swallowed: the browser denies
 * the request when there's no user gesture or the element isn't ready, and
 * neither is actionable here.
 */
export async function togglePip(): Promise<void> {
    const video = getVideoElement()
    if (document.pictureInPictureElement === video) {
        await document.exitPictureInPicture().catch(() => {})
    } else {
        await video.requestPictureInPicture().catch(() => {})
    }
}

/**
 * Whether the app is currently fullscreen. Shared so both the stage
 * overlay's fullscreen button and the transport's video-settings menu
 * reflect the same state.
 */
const stageFullscreen$ = externalData(document.fullscreenElement != null, (setData) =>
    documentEvents.on('fullscreenchange', () => setData(document.fullscreenElement != null))
)

export function getStageFullscreen(): ObservableValue<boolean> {
    return stageFullscreen$
}

/**
 * Fullscreen the AppView wrapper (`#fullscreenRoot`) so the transport stays
 * visible alongside the picture, or exit if already fullscreen. Rejections
 * are swallowed: the browser denies the request without a user gesture.
 */
export function toggleStageFullscreen(): void {
    if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
    } else {
        const target =
            document.querySelector<HTMLElement>('#fullscreenRoot') ??
            getVideoElement().parentElement
        target?.requestFullscreen().catch(() => {})
    }
}
